import { inject, Injectable } from '@angular/core';
import { defer, forkJoin, Observable, of } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { BaseAuthTokensStorageService } from './base-auth-tokens-storage.service';
import { StorageService } from '@dta/shared/services/storage/storage.service';
import { TokenType, TokenTypeValue } from '@shared/shared/auth/common/constants/token-type';
import { AUTH_TOKENS_STORAGE_CONFIGURATION } from '@shared/shared/auth/data-access/auth-tokens-storage-configuration';
import { fromUnixTime } from 'date-fns';
import { jwtDecode } from 'jwt-decode';

interface UserSession {
  expiration_at: Date;
  created_at: Date;
  user_id: string;
}

interface UserSessionJWT {
  exp: number;
  iat: number;
  u_id: string;
}

@Injectable()
export class AuthTokensStorageService extends BaseAuthTokensStorageService {
  protected readonly storageService: StorageService = inject(StorageService);
  protected readonly authTokensStorageConfiguration: Record<TokenTypeValue, string> = inject(
    AUTH_TOKENS_STORAGE_CONFIGURATION
  );

  storeToken(userId: string, tokenType: TokenTypeValue, token: string): Observable<void> {
    return defer(() => {
      this.storageService.setItem(
        this.storageService.getKey(userId, this.authTokensStorageConfiguration[tokenType]),
        token
      );
      return of(undefined);
    });
  }

  readToken(userId: string, tokenType: TokenTypeValue): Observable<string | null> {
    return defer(() => {
      let token: string | null;
      try {
        const key = this.storageService.getKey(userId, this.authTokensStorageConfiguration[tokenType]);

        // Try plain first (new tokens); legacy encrypted tokens are raw base64 and fail JSON.parse,
        // so fall back to forge-decrypt for those.
        token = this.storageService.getItem(key);
        if (!AuthTokensStorageService.createFromJWT(token)) {
          token = this.storageService.getParsedItem(key, true);
        }

        if (AuthTokensStorageService.createFromJWT(token)) {
          return of(token);
        }
      } catch (error) {
        // An unexpected read/decode failure must NOT be treated as an absent token. Falling through
        // to removeToken here would wipe a possibly-valid token and, via the auth resolution flow,
        // permanently remove the account from the switcher. Log and return null without removing,
        // so a later retry can still succeed.
        console.error('AuthTokensStorageService.readToken failed unexpectedly', error);
        return of(null);
      }

      // Read succeeded but the token is genuinely missing or unparseable — safe to clear it.
      return this.removeToken(userId, tokenType).pipe(mapTo(null));
    });
  }

  removeToken(userId: string, tokenType: TokenTypeValue): Observable<void> {
    return defer(() => {
      try {
        this.storageService.removeItem(
          this.storageService.getKey(userId, this.authTokensStorageConfiguration[tokenType])
        );
      } catch {
        // noop
      }
      return of(undefined);
    });
  }

  removeAllTokens(userId: string): Observable<void> {
    return forkJoin(
      Object.keys(this.authTokensStorageConfiguration).map(tokenType =>
        this.removeToken(userId, tokenType as TokenTypeValue)
      )
    ).pipe(mapTo(undefined));
  }

  getBearerToken(userId: string, forceRefreshToken: boolean = false): Observable<string | null> {
    return forkJoin([this.readToken(userId, TokenType.refresh), this.readToken(userId, TokenType.access)]).pipe(
      map(([refreshToken, accessToken]) => {
        if (forceRefreshToken) {
          return refreshToken ? `Bearer ${refreshToken}` : null;
        }
        return accessToken ? `Bearer ${accessToken}` : null;
      })
    );
  }

  static createFromJWT(token: string): UserSession | null {
    try {
      const decoded = jwtDecode<UserSessionJWT>(token);
      return {
        expiration_at: fromUnixTime(decoded.exp),
        created_at: fromUnixTime(decoded.iat),
        user_id: decoded.u_id
      };
    } catch {
      return null;
    }
  }
}
